import { Col, Row } from "react-bootstrap";
import { Facility, TimeSlot } from "../../types/Structure";
import { FacilityType } from "../../enums/FacilityType";
import "./Calendar.css";
import classNames from "classnames";
import {
  CompletePadelResult,
  CompletePickleballResult,
  CompleteTennisResult,
} from "../../types/Result";
import { CompleteBooking } from "../../types/Booking";
import { getBookingTypeLabel, isPastDateTime } from "../../utils/stringUtil";
import BookingBlock from "./BookingBlock";
import { Dayjs } from "dayjs";
import { useDrag, useDrop } from "react-dnd";
import { ReactNode, useCallback } from "react";
import { Bookings } from "../../api/Booking";
import { useToasts } from "../../contexts/toasts";
import { Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

const getMatchPlayer1Team1Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player1Team1
    ? `${match.player1Team1?.name || "-"} ${match.player1Team1?.familyName}`
    : "-";
};

const getMatchPlayer2Team1Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player2Team1
    ? `${match.player2Team1?.name || "-"} ${match.player2Team1?.familyName}`
    : "-";
};

const getMatchPlayer1Team2Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player1Team2
    ? `${match.player1Team2?.name || "-"} ${match.player1Team2?.familyName}`
    : "-";
};

const getMatchPlayer2Team2Name = (
  match:
    | CompletePadelResult
    | CompleteTennisResult
    | CompletePickleballResult
    | undefined
) => {
  return match && match.player2Team2
    ? `${match.player2Team2?.name || "-"} ${match.player2Team2?.familyName}`
    : "-";
};

type CalendarProps = {
  facility: Facility;
  onItemClick: (
    time: TimeSlot,
    facility: Facility,
    booking?: CompleteBooking
  ) => void;
  date: Dayjs;
  onReset?: () => void;
};

type DraggableBookingProps = {
  children: ReactNode;
  booking: CompleteBooking;
};

export const DraggableBooking: React.FC<DraggableBookingProps> = ({
  children,
  booking,
}) => {
  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: "BOOKING",
    item: { booking },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  const renderBookingTooltip = useCallback(
    () => (
      <div className="justify-content-center text-center">
        <small>
          <strong className="mb-2">{booking.time}</strong>
        </small>
        {booking.customType && (
          <Row className="text-center g-0">
            <small className="truncate-text">
              <strong>{booking.customType}</strong>
            </small>
          </Row>
        )}
        {booking.match ? (
          <div>
            {![1, 2, 7].includes(booking.facility.sportId) ? (
              <Row className="text-center g-0">
                <small className="truncate-text">
                  <strong>{booking.notes || ""}</strong>
                </small>
              </Row>
            ) : (
              <>
                {!booking.match?.player1Team1Id &&
                !booking.match?.player2Team1Id &&
                !booking.match?.player1Team2Id &&
                !booking.match?.player2Team2Id ? (
                  <Row className="text-center g-0">
                    <small className="truncate-text">
                      <strong>{booking.notes || ""}</strong>
                    </small>
                  </Row>
                ) : (
                  <>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer1Team1Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer2Team1Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer1Team2Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    <Row className="text-center g-0">
                      <small className="truncate-text">
                        <strong>
                          {getMatchPlayer2Team2Name(booking.match)}
                        </strong>
                      </small>
                    </Row>
                    {booking.notes && booking.notes !== "" && (
                      <>
                        <Row className="text-center g-0">
                          <strong>Note: </strong>
                        </Row>
                        <Row className="text-center g-0">
                          <span>{booking.notes}</span>
                        </Row>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        ) : (
          <Row className="text-center g-0">
            <small className="truncate-text">
              <strong>
                {booking.createdByHost
                  ? booking.customType
                    ? booking.notes || ""
                    : !booking.notes || booking.notes === ""
                    ? `${getBookingTypeLabel(booking)}`
                    : booking.notes + ""
                  : `${booking.user.name} ${booking.user.familyName}`}
              </strong>
            </small>
          </Row>
        )}
      </div>
    ),
    [booking]
  );

  return (
    <Tooltip title={renderBookingTooltip()}>
      <div
        ref={dragRef}
        style={{ opacity: isDragging ? 0.5 : 1, cursor: "move" }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

type DropTargetSlotProps = {
  slot: TimeSlot;
  facility: Facility;
  onDropBooking: (
    booking: CompleteBooking,
    slot: TimeSlot,
    facility: Facility
  ) => void;
  children: ReactNode;
};

type DragItem = {
  booking: CompleteBooking;
};

export const DropTargetSlot: React.FC<DropTargetSlotProps> = ({
  onDropBooking,
  slot,
  facility,
  children,
}) => {
  const [{ isOver }, dropRef] = useDrop<DragItem, void, { isOver: boolean }>(
    () => ({
      accept: "BOOKING",
      drop: (item) => onDropBooking(item.booking, slot, facility),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    })
  );

  return (
    <div
      ref={dropRef}
      style={{
        backgroundColor: isOver ? "#e0f7fa" : undefined,
        transition: "background-color 0.2s ease",
      }}
    >
      {children}
    </div>
  );
};

export function Calendar({
  facility,
  date,
  onItemClick,
  onReset,
}: CalendarProps) {
  const { showToast } = useToasts();
  const navigate = useNavigate();

  const dayOfWeek = date.day(); // 0 = Domenica, 6 = Sabato

  // Prendiamo solo il timeSlot del giorno corrente
  const todaySchedule = facility.timeSlots.find(
    (schedule) => schedule.dayOfWeek === dayOfWeek && schedule.enabled
  );

  const slotsForToday = todaySchedule?.slots || [];

  const isBooked = (timeslot: string) => {
    return facility.bookings?.some(
      (booking) =>
        booking.date === date.format("YYYY-MM-DD") &&
        booking.time.split("-")[0] === timeslot
    );
  };

  const handleItemClick = (timeSlot: TimeSlot) => (e: React.MouseEvent) => {
    e.preventDefault();

    const startTime = timeSlot.time.split("-")[0];

    if (
      !isBooked(startTime) &&
      isPastDateTime(date.format("YYYY-MM-DD"), startTime)
    ) {
      return;
    }

    const matchedBooking = facility.bookings?.find(
      (booking) =>
        booking.date === date.format("YYYY-MM-DD") &&
        booking.time.split("-")[0] === startTime
    );

    onItemClick(timeSlot, facility, matchedBooking);
  };

  const handleBookingDrop = useCallback(
    (
      booking: CompleteBooking,
      newTime: TimeSlot,
      selectedFacility: Facility
    ) => {
      const matchedBooking = selectedFacility.bookings?.find(
        (b) =>
          b.date === date.format("YYYY-MM-DD") &&
          b.time.split("-")[0] === newTime.time.split("-")[0]
      );

      if (!matchedBooking) {
        Bookings.updateBooking(booking.id, {
          facilityId: facility.id!,
          time: newTime.time,
        })
          .then(() => {
            showToast(
              "booking-update-success",
              "Prenotazione correttamente spostata",
              "",
              "success"
            );
            onReset?.();
          })
          .catch(() => {
            showToast(
              "booking-update-error",
              "Errore nello spostamento della prenotazione",
              "",
              "danger"
            );
          });
      } else {
        showToast(
          "booking-update-error",
          "Campo già prenotato, impossibile spostare la prenotazione selezionata",
          "",
          "danger"
        );
      }
    },
    [date, facility, onReset, showToast]
  );

  const handleNavigateToBulk = useCallback(() => {
    navigate(`${facility.id}/ricorrenti`);
  }, [navigate, facility]);

  return (
    <div>
      <Row className="align-items-center">
        <Col xs={12} className="mb-2 text-center">
          <span>
            <strong>
              {facility.name} (
              {facility.type === FacilityType.Indoor ? "Indoor" : "Outdoor"})
            </strong>
            <Tooltip title="Programma prenotazione ricorrente">
              <button
                onClick={handleNavigateToBulk}
                className="circle-button ms-2"
              >
                <FontAwesomeIcon icon={faCalendar} />
              </button>
            </Tooltip>
          </span>
        </Col>

        <Col xs={12} sm={12} className="timeslot-col justify-content-center">
          {slotsForToday.map((slot, i) => {
            const startTime = slot.time.split("-")[0];
            const matchedBooking = facility.bookings?.find(
              (booking) =>
                booking.date === date.format("YYYY-MM-DD") &&
                booking.time.split("-")[0] === startTime
            );
            const booked = !!matchedBooking;

            return (
              <DropTargetSlot
                key={i}
                slot={slot}
                facility={facility}
                onDropBooking={handleBookingDrop}
              >
                {booked && matchedBooking ? (
                  <DraggableBooking booking={matchedBooking}>
                    <BookingBlock
                      booking={matchedBooking}
                      onClick={handleItemClick(slot)}
                      className="slot-block"
                    />
                  </DraggableBooking>
                ) : (
                  <div
                    onClick={handleItemClick(slot)}
                    className={classNames("bk-free slot-block", {
                      "bk-free--disabled": isPastDateTime(
                        date.format("YYYY-MM-DD"),
                        startTime
                      ),
                    })}
                  >
                    <span className="bk-free-time">{startTime}</span>
                    <span className="bk-free-cta">+ Prenota</span>
                  </div>
                )}
              </DropTargetSlot>
            );
          })}
        </Col>
      </Row>
    </div>
  );
}
